// Copied/adapted from Fanxing Dev's floating support UI; owned and built by this repo.
import { Headphones, LoaderCircle, MessageCircle, Send, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import BackToTop from './BackToTop';
import {
  CUSTOMER_SERVICE_SITE, VisitorChatError, mergeVisitorMessages, readVisitorCredentials,
  saveVisitorCredentials, visitorRequest,
  type VisitorCredentials, type VisitorMessage, type VisitorSession,
} from './visitorChatApi';
import './visitor-chat.css';

export default function VisitorChatWidget({ hidden = false }: { hidden?: boolean }) {
  const [open, setOpen] = useState(false);
  const [credentials, setCredentials] = useState<VisitorCredentials | null>(null);
  const [session, setSession] = useState<VisitorSession | null>(null);
  const [messages, setMessages] = useState<VisitorMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [sending, setSending] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [sendError, setSendError] = useState('');
  const [unread, setUnread] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const connectingRef = useRef(false);
  const sendingRef = useRef(false);
  const openRef = useRef(false);
  const sequence = useRef(0);
  const pending = useRef<{ text: string; id: string } | null>(null);
  openRef.current = open && !hidden;

  const persist = (value: VisitorCredentials | null) => {
    try { saveVisitorCredentials(window.localStorage, value); } catch { /* Storage may be blocked. */ }
  };
  const connect = useCallback(async () => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    setConnecting(true);
    setConnectionError('');
    let stored: VisitorCredentials | null = null;
    try { stored = readVisitorCredentials(window.localStorage); } catch { /* New tab-only session. */ }
    const request = (resume: VisitorCredentials | null) => visitorRequest<{ token: string; session: VisitorSession }>('/sessions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteKey: CUSTOMER_SERVICE_SITE, ...resume }),
    });
    try {
      let result;
      try { result = await request(stored); }
      catch (error) {
        // A temporary outage must not discard a valid visitor's conversation.
        if (!stored || !(error instanceof VisitorChatError) || error.code !== 'VISITOR_SESSION_UNAUTHORIZED') throw error;
        persist(null);
        result = await request(null);
      }
      const next = { sessionId: result.session.id, token: result.token };
      persist(next);
      sequence.current = 0;
      setMessages([]);
      setCredentials(next);
      setSession(result.session);
    } catch { setConnectionError('暂时无法连接咨询老师，请稍后重试。'); }
    finally { connectingRef.current = false; setConnecting(false); }
  }, []);

  useEffect(() => {
    try { if (readVisitorCredentials(window.localStorage)) void connect(); } catch { /* No persisted session. */ }
  }, [connect]);
  useEffect(() => { if (hidden) setOpen(false); }, [hidden]);
  useEffect(() => {
    const show = () => { setOpen(true); setUnread(0); if (!credentials) void connect(); };
    window.addEventListener('duanju:open-support', show);
    return () => window.removeEventListener('duanju:open-support', show);
  }, [connect, credentials]);
  useEffect(() => {
    if (!open || hidden) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', keyboard);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', keyboard); };
  }, [open, hidden]);
  useEffect(() => {
    if (open && credentials) input.current?.focus();
  }, [open, credentials]);

  useEffect(() => {
    if (!credentials) return;
    let active = true;
    let polling = false;
    const poll = async () => {
      if (!active || polling || document.hidden) return;
      polling = true;
      try {
        const payload = await visitorRequest<{ messages: VisitorMessage[]; nextAfter: number }>(
          `/sessions/${encodeURIComponent(credentials.sessionId)}/messages?after=${sequence.current}&limit=200`,
          { headers: { 'X-Customer-Service-Token': credentials.token } },
        );
        if (!active) return;
        const next = payload.messages;
        sequence.current = Math.max(sequence.current, payload.nextAfter || 0, ...next.map(message => message.sequence));
        setMessages(current => mergeVisitorMessages(current, next));
        if (!openRef.current) setUnread(current => current + next.filter(message => !message.mine).length);
        setConnectionError('');
      } catch (error) {
        if (!active) return;
        if (error instanceof VisitorChatError && error.code === 'VISITOR_SESSION_UNAUTHORIZED') {
          persist(null); setCredentials(null); setSession(null);
          setConnectionError('咨询会话已过期，请重新连接。');
        } else setConnectionError('连接暂时中断，正在重试；已有消息会保留。');
      } finally { polling = false; }
    };
    void poll();
    const timer = window.setInterval(poll, open ? 1500 : 5000);
    document.addEventListener('visibilitychange', poll);
    return () => { active = false; window.clearInterval(timer); document.removeEventListener('visibilitychange', poll); };
  }, [credentials, open]);
  useEffect(() => {
    if (open) list.current?.scrollTo({ top: list.current.scrollHeight });
  }, [messages.length, open]);

  const send = async () => {
    const text = draft.trim();
    if (!credentials || !text || sendingRef.current) return;
    // Retry an uncertain send with the same id, so a timeout never duplicates a message.
    if (pending.current?.text !== text) pending.current = { text, id: `web-${Date.now()}-${Array.from(crypto.getRandomValues(new Uint32Array(4)), value => value.toString(16)).join('')}` };
    sendingRef.current = true; setSending(true); setSendError('');
    try {
      const payload = await visitorRequest<{ message: VisitorMessage }>(`/sessions/${encodeURIComponent(credentials.sessionId)}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Customer-Service-Token': credentials.token },
        body: JSON.stringify({ clientMessageId: pending.current.id, text }),
      });
      setMessages(current => mergeVisitorMessages(current, [payload.message]));
      // Do not advance the polling cursor here: a teacher's earlier reply may still be unread.
      setDraft(''); pending.current = null;
    } catch { setSendError('消息发送未确认，内容已保留，请重试。'); }
    finally { sendingRef.current = false; setSending(false); input.current?.focus(); }
  };

  return <div className="visitor-support" ref={root} hidden={hidden}>
    {open && <section id="visitor-support-panel" className="visitor-support-panel" aria-label="在线咨询">
      <header className="visitor-support-header">
        <Headphones size={22} aria-hidden="true" />
        <div><h2>在线咨询</h2><p>繁星短剧训练营 · 咨询老师</p></div>
        <button type="button" aria-label="关闭在线咨询" onClick={() => { setOpen(false); trigger.current?.focus(); }}><X size={20} /></button>
      </header>
      <div className="visitor-support-intro">课程、班次或报名问题，都可以在这里留言。老师回复后会显示在本对话中。{session && <small>访客 #{session.visitorCode}</small>}</div>
      <div className="visitor-support-messages" ref={list} role="log" aria-label="咨询消息" aria-live="polite">
        {connecting ? <p className="visitor-support-state"><LoaderCircle className="support-spinner" size={18} />正在连接咨询老师…</p> : null}
        {!connecting && !credentials ? <div className="visitor-support-state"><p role="alert">{connectionError}</p><button type="button" onClick={() => void connect()}>重新连接</button></div> : null}
        {credentials && !messages.length && <p className="visitor-support-state">现在可以开始咨询了</p>}
        {messages.map(message => <article key={message.id} className={message.mine ? 'visitor-message mine' : 'visitor-message'}>
          <small>{message.mine ? '我' : message.senderName || '咨询老师'}</small><p>{message.text}</p>
          <time dateTime={message.createdAt}>{new Date(message.createdAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })}</time>
        </article>)}
      </div>
      <form className="visitor-support-composer" onSubmit={event => { event.preventDefault(); void send(); }}>
        {(sendError || (credentials && connectionError)) && <p role="alert">{sendError || connectionError}</p>}
        <div><textarea ref={input} rows={1} maxLength={1000} aria-label="咨询内容" placeholder="输入你想咨询的内容" value={draft} disabled={!credentials || sending} onChange={event => setDraft(event.target.value)} onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); }
        }} /><button type="submit" aria-label="发送消息" disabled={!credentials || !draft.trim() || sending}>{sending ? <LoaderCircle className="support-spinner" size={18} /> : <Send size={18} />}</button></div>
      </form>
    </section>}
    <div className="visitor-support-actions">
      <BackToTop hidden={hidden} />
      <button ref={trigger} type="button" className="visitor-support-trigger" aria-controls="visitor-support-panel" aria-expanded={open} onClick={() => {
        setOpen(!open); setUnread(0);
        if (!open && !credentials) void connect();
      }}><span><MessageCircle size={20} aria-hidden="true" />{unread > 0 && <b aria-label={`${unread}条未读消息`}>{Math.min(unread, 99)}</b>}</span>在线咨询</button>
    </div>
  </div>;
}
